import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";
import { type AuditLogEntry, toAuditLogEntry } from "@/api/models";
import { env } from "@/lib/env";
import { mockAuditLog } from "@/api/mock";

export type AuditLogResult = {
  items: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
};

export function useAuditLog(
  params: {
    actor?: string;
    action?: string;
    from?: string;
    to?: string;
    page?: number;
  } = {},
) {
  // Scope the cache by the active department so a respai_admin switching
  // departments never sees the previous tenant's audit log from cache (the
  // request is dept-scoped via the X-RespAi-Department-ID header).
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["audit-log", deptId, params],
    queryFn: async (): Promise<AuditLogResult> => {
      if (env.e2eMockApi) return mockAuditLog();
      // The backend filters by actor_user_id (UUID) and action verb; the page's
      // "Actor"/"Action" inputs are free-text, so we pull the dept-scoped page
      // (RLS-narrowed, newest-first) and narrow client-side by substring.
      const rows = await api.get<components["schemas"]["AuditLogRead"][]>(
        "/audit-logs",
        { query: { limit: 200 } },
      );
      let items = rows.map(toAuditLogEntry);
      const actorQ = params.actor?.trim().toLowerCase();
      const actionQ = params.action?.trim().toLowerCase();
      if (actorQ) {
        items = items.filter(
          (e) =>
            (e.actor_user_id ?? "").toLowerCase().includes(actorQ) ||
            (e.actor_display_name ?? "").toLowerCase().includes(actorQ),
        );
      }
      if (actionQ) {
        items = items.filter((e) => e.action.toLowerCase().includes(actionQ));
      }
      return {
        items,
        total: items.length,
        page: params.page ?? 1,
        page_size: items.length,
      };
    },
  });
}
